/**
 * ContentPlanWizard — SERP-driven 30-day content plan wizard.
 *
 * Two-step modal:
 *   Step 1 — Inputs: seed keywords, locale, cadence, publishing channel
 *   Step 2 — Review table: inline edit, drag-to-reorder, regenerate per row,
 *             auto-save on blur/reorder/delete
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GripVertical,
  AlertTriangle,
  RefreshCw,
  Trash2,
  X,
  Check,
  Info,
} from 'lucide-react';
import { GlassModal } from '../ui/GlassModal';
import { GlassButton } from '../ui/GlassButton';
import {
  generateContentPlan,
  quickstartGeneratePlan,
  quickstartApprovePlan,
  quickstartPatchPlan,
  getContentPlanDraft,
  patchContentPlanDraft,
  regenerateContentPlanRow,
  approveContentPlan,
  discardContentPlanDraft,
  getMasterplanStreams,
  getSocialPlanCandidates,
  getSocialAccounts,
  listSiteProjects,
  ContentPlanDraft,
  ContentPlanItem,
  ContentPlanApproveResponse,
  SocialPlanCandidatesResponse,
  SiteProjectSummary,
} from '../../services/api';
import './ContentPlanWizard.css';

// ---------------------------------------------------------------------------
// Types & helpers
// ---------------------------------------------------------------------------

type SyncState = 'idle' | 'saving' | 'saved' | 'error';

type CadenceOption = 'daily' | '3x_week' | 'weekly';
type ChannelOption = 'wordpress' | 'webflow' | 'aime_site' | 'social';

const CADENCE_LABELS: Record<CadenceOption, string> = {
  daily: 'Daily',
  '3x_week': '3× week',
  weekly: 'Weekly',
};

const LOCALE_OPTIONS = [
  { value: 'en-AU', label: 'English (AU)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (GB)' },
  { value: 'en-CA', label: 'English (CA)' },
  { value: 'en-NZ', label: 'English (NZ)' },
  { value: 'en', label: 'English (generic)' },
];

const CHANNEL_OPTIONS: { value: ChannelOption; label: string }[] = [
  { value: 'wordpress', label: 'WordPress' },
  { value: 'webflow', label: 'Webflow' },
  { value: 'aime_site', label: 'AiMe Site' },
  { value: 'social', label: 'Social (schedule existing posts)' },
];

/**
 * Convert a UTC ISO string to a local datetime-local input value
 * using the provided IANA timezone string.
 */
function utcToLocalInput(utcIso: string, tzName: string): string {
  try {
    const dt = new Date(utcIso);
    const formatter = new Intl.DateTimeFormat('sv-SE', {
      timeZone: tzName,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    // sv-SE gives YYYY-MM-DD HH:MM — replace space with T for datetime-local
    return formatter.format(dt).replace(' ', 'T');
  } catch {
    return utcIso.replace('Z', '').substring(0, 16);
  }
}

/**
 * Convert a datetime-local value (treated as the project timezone) back to UTC ISO Z.
 */
function localInputToUtcZ(localValue: string, tzName: string): string {
  try {
    // localValue is like "2026-05-21T09:00"
    // We need to interpret it as being in tzName, then convert to UTC
    const parts = localValue.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/);
    if (!parts) return localValue + ':00Z';
    const dtStr = parts[1] + 'T' + parts[2] + ':00';
    // Use Intl to find the UTC offset at that moment in tzName
    const testDt = new Date(dtStr + 'Z'); // treat as UTC initially for offset calc
    const tzFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tzName,
      timeZoneName: 'shortOffset',
    });
    const formattedParts = tzFormatter.formatToParts(testDt);
    const offsetPart = formattedParts.find((p) => p.type === 'timeZoneName')?.value || '';
    // Parse offset like "GMT+10", "GMT-5:30"
    const offsetMatch = offsetPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (!offsetMatch) {
      // Fallback: assume the value is UTC
      return dtStr + 'Z';
    }
    const sign = offsetMatch[1] === '+' ? -1 : 1;
    const hours = parseInt(offsetMatch[2], 10);
    const minutes = parseInt(offsetMatch[3] || '0', 10);
    const totalOffsetMinutes = sign * (hours * 60 + minutes);
    const dtUtc = new Date(new Date(dtStr + 'Z').getTime() + totalOffsetMinutes * 60000);
    return dtUtc.toISOString().replace('.000Z', 'Z').replace(/\.\d{3}Z$/, 'Z');
  } catch {
    return localValue + ':00Z';
  }
}

/** Format a UTC ISO string for compact display in project tz. */
function formatDisplayDate(utcIso: string, tzName: string): string {
  try {
    const dt = new Date(utcIso);
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: tzName,
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dt);
  } catch {
    return utcIso;
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ContentPlanWizardProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  // Inline mode — all optional, default to existing modal behaviour
  inline?: boolean;                      // when true, render without GlassModal wrapper
  forceGeminiOnly?: boolean;             // when true, pass force_gemini_only:true in generate request
  endpointOverrides?: {
    /** When set (truthy), the quickstartGeneratePlan service function is used instead of generateContentPlan. The URL string itself is not used as a fetch path — it acts as a discriminator. Pass the canonical onboarding URL for clarity. */
    generate?: string | undefined;
    /** When set (truthy), the quickstartApprovePlan service function is used instead of approveContentPlan. Same discriminator semantics as generate. */
    approve?: string | undefined;
    /** When set (truthy), the quickstartPatchPlan service function is used instead of patchContentPlanDraft. This allows trial users editing rows in inline mode to bypass the seo-gated PATCH endpoint. */
    patch?: string | undefined;
  };
  autoStart?: boolean;                   // when true, skip step 1 and call generate on mount
  initialSeeds?: string[];               // seeds to pre-fill when autoStart is true
  initialLocale?: string;
  initialCadence?: 'daily' | '3x_week' | 'weekly';
  initialChannel?: 'wordpress' | 'webflow' | 'aime_site' | 'social';
  onApproveSuccess?: (result: ContentPlanApproveResponse) => void;  // called after successful approve
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ContentPlanWizard: React.FC<ContentPlanWizardProps> = ({
  isOpen,
  onClose,
  projectId,
  inline = false,
  forceGeminiOnly = false,
  endpointOverrides,
  autoStart = false,
  initialSeeds,
  initialLocale,
  initialCadence,
  initialChannel,
  onApproveSuccess,
}) => {
  const navigate = useNavigate();

  // ── Wizard step ────────────────────────────────────────────────
  const [step, setStep] = useState<1 | 2>(1);

  // ── Step 1 state ───────────────────────────────────────────────
  const [seeds, setSeeds] = useState<string[]>(initialSeeds || []);
  const [chipInput, setChipInput] = useState('');
  const [locale, setLocale] = useState<string>(initialLocale || 'en-AU');
  const [cadence, setCadence] = useState<CadenceOption>(initialCadence || '3x_week');
  const [channel, setChannel] = useState<ChannelOption>(initialChannel || 'wordpress');
  const [loadingSeeds, setLoadingSeeds] = useState(false);
  const isSocial = channel === 'social';
  const isAimeSite = channel === 'aime_site';
  // Quickstart/onboarding mode wires a dedicated generate endpoint
  // (onboarding_routes.quickstart_generate_plan) that only accepts
  // 'wordpress' | 'webflow' — offering Social or AiMe Site there is a
  // guaranteed 400 (onboarding has no repurposed posts and no site yet).
  const availableChannelOptions = endpointOverrides?.generate
    ? CHANNEL_OPTIONS.filter((opt) => opt.value !== 'social' && opt.value !== 'aime_site')
    : CHANNEL_OPTIONS;

  // ── Social channel: candidate discovery (step 1) + account picker (step 2)
  const [socialCandidates, setSocialCandidates] = useState<SocialPlanCandidatesResponse | null>(null);
  const [loadingSocialCandidates, setLoadingSocialCandidates] = useState(false);
  const [socialAccountsByPlatform, setSocialAccountsByPlatform] = useState<Record<string, any[]>>({});

  // ── AiMe Site channel: site picker (step 1) + "Publishing to" (step 2) ──
  const [siteProjects, setSiteProjects] = useState<SiteProjectSummary[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const needsBlogPage = (s: SiteProjectSummary) =>
    (s.source_type || 'component') === 'imported_static' && !s.has_blog_index;
  const eligibleSites = useMemo(
    () => siteProjects.filter(s => s.status === 'published' || s.status === 'draft'),
    [siteProjects]
  );
  const selectableSites = useMemo(
    () => eligibleSites.filter(s => !needsBlogPage(s)),
    [eligibleSites]
  );

  // ── Generating state ───────────────────────────────────────────
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // ── Conflict (already approved draft) ─────────────────────────
  const [conflict, setConflict] = useState<{
    approved_at: string;
    task_ids: string[];
    publication_ids: string[];
  } | null>(null);

  // ── Step 2 state ───────────────────────────────────────────────
  const [draft, setDraft] = useState<ContentPlanDraft | null>(null);
  const [items, setItems] = useState<ContentPlanItem[]>([]);
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [showTooltip, setShowTooltip] = useState(false);
  const [regeneratingRowId, setRegeneratingRowId] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);

  // ── Debounce timer ─────────────────────────────────────────────
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Drag state ─────────────────────────────────────────────────
  const dragSourceIndex = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // ── Toast ──────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const showToast = useCallback((msg: string, kind: 'success' | 'error' = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Load masterplan seeds on open (or auto-start) ─────────────
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setChipInput('');
    setGenerateError(null);
    setConflict(null);
    setDraft(null);
    setItems([]);
    setSyncState('idle');
    setSelectedSiteId('');
    setSiteProjects([]);

    if (autoStart && initialSeeds && initialSeeds.length > 0) {
      // autoStart: pre-fill seeds from provided initial values, then generate immediately
      setSeeds(initialSeeds);
      setLocale(initialLocale || 'en-AU');
      setCadence(initialCadence || '3x_week');
      setChannel(initialChannel || 'wordpress');
      setLoadingSeeds(false);
      // autoStartFired effect below will call handleGenerate after seeds are set
    } else if (initialSeeds && initialSeeds.length > 0) {
      // Caller-provided seeds win over masterplan auto-load (e.g. cannibalization handoff).
      // Skip the getMasterplanStreams fetch entirely so the prefill is not overwritten.
      setSeeds(initialSeeds);
      setLoadingSeeds(false);
    } else {
      setSeeds([]);
      setLoadingSeeds(true);
      getMasterplanStreams(projectId)
        .then((res) => {
          const autoSeeds = (res.streams || []).filter(Boolean).slice(0, 10);
          setSeeds(autoSeeds);
        })
        .catch(() => {/* no masterplan — start empty */})
        .finally(() => setLoadingSeeds(false));
    }
  }, [isOpen, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── autoStart: trigger generate after seeds are set ───────────
  const autoStartFired = useRef(false);
  useEffect(() => {
    if (!isOpen || !autoStart) return;
    if (autoStartFired.current) return;
    if (seeds.length === 0) return;
    autoStartFired.current = true;
    handleGenerate(); // call with current seeds
  }); // no deps — runs after every render until autoStartFired

  // ── Social channel: fetch candidate count for step-1 summary/empty-state ──
  useEffect(() => {
    if (!isOpen || step !== 1 || channel !== 'social') return;
    let cancelled = false;
    setLoadingSocialCandidates(true);
    getSocialPlanCandidates(projectId)
      .then((res) => { if (!cancelled) setSocialCandidates(res); })
      .catch(() => { if (!cancelled) setSocialCandidates(null); })
      .finally(() => { if (!cancelled) setLoadingSocialCandidates(false); });
    return () => { cancelled = true; };
  }, [isOpen, step, channel, projectId]);

  // ── AiMe Site channel: fetch this project's sites for the step-1 picker ──
  useEffect(() => {
    if (!isOpen || step !== 1 || channel !== 'aime_site') return;
    let cancelled = false;
    setLoadingSites(true);
    listSiteProjects(projectId)
      .then((sites) => { if (!cancelled) setSiteProjects(sites); })
      .catch(() => { if (!cancelled) setSiteProjects([]); })
      .finally(() => { if (!cancelled) setLoadingSites(false); });
    return () => { cancelled = true; };
  }, [isOpen, step, channel, projectId]);

  // Auto-select the first selectable site when none is chosen yet.
  useEffect(() => {
    if (channel !== 'aime_site' || selectedSiteId) return;
    if (selectableSites.length > 0) setSelectedSiteId(selectableSites[0].site_project_id);
  }, [channel, selectedSiteId, selectableSites]);

  // ── Chip input handling ────────────────────────────────────────
  const addChip = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || seeds.includes(trimmed) || seeds.length >= 15) return;
    setSeeds((prev) => [...prev, trimmed]);
    setChipInput('');
  };

  const handleChipKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addChip(chipInput);
    } else if (e.key === 'Backspace' && !chipInput && seeds.length > 0) {
      setSeeds((prev) => prev.slice(0, -1));
    }
  };

  const removeChip = (idx: number) => {
    setSeeds((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Generate plan ──────────────────────────────────────────────
  const handleGenerate = async () => {
    if (isSocial) {
      if ((socialCandidates?.total_eligible ?? 0) === 0) return;
    } else if (!seeds.length) {
      return;
    } else if (isAimeSite && !selectedSiteId) {
      return;
    }
    setGenerating(true);
    setGenerateError(null);
    setConflict(null);

    try {
      let result: ContentPlanDraft;
      if (endpointOverrides?.generate) {
        // Use the quickstart onboarding wrapper endpoint (never offers
        // aime_site — no site_project_id to send)
        result = await quickstartGeneratePlan({
          project_id: projectId,
          seeds: isSocial ? [] : seeds,
          locale,
          cadence,
          channel,
        });
      } else {
        result = await generateContentPlan({
          project_id: projectId,
          seeds: isSocial ? [] : seeds,
          locale,
          cadence,
          channel,
          ...(forceGeminiOnly && !isSocial ? { force_gemini_only: true } : {}),
          ...(isAimeSite ? { site_project_id: selectedSiteId } : {}),
        });
      }
      setDraft(result);
      setItems(result.items || []);
      setStep(2);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        const data = err?.response?.data?.detail || err?.response?.data;
        setConflict({
          approved_at: data?.approved_at || '',
          task_ids: data?.task_ids || [],
          publication_ids: data?.publication_ids || [],
        });
      } else if (status === 403 && err?.response?.data?.detail === 'Editor access required for this project') {
        // seo_routes._require_project_access's exact detail string — distinguishes
        // the view-only-team-member case from the unrelated "module not permitted"
        // 403 that require_module_access('seo') raises with a different detail.
        setGenerateError(
          'You have view-only access to this project. Ask the project owner or your team admin for edit access to generate a plan.'
        );
      } else if (status === 404 && err?.response?.data?.detail === 'Project not found') {
        // Same idea: seo_routes' project-access 404 has this exact detail; other
        // 404s (e.g. "Site not found" for a deleted AiMe site) keep their own text.
        setGenerateError('This project is no longer available. Reload and pick a project again.');
      } else {
        const detail = err?.response?.data?.detail;
        setGenerateError(
          typeof detail === 'string'
            ? detail
            : detail?.detail || 'Failed to generate plan. Please try again.'
        );
      }
    } finally {
      setGenerating(false);
    }
  };

  // ── Auto-save (debounced) ──────────────────────────────────────
  const scheduleSave = useCallback(
    (newItems: ContentPlanItem[]) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      setSyncState('saving');
      saveTimer.current = setTimeout(async () => {
        try {
          // Use the onboarding patch wrapper when endpointOverrides.patch is set.
          // This lets trial users editing rows in inline/onboarding mode bypass
          // the seo-gated PATCH /api/seo/content-plan/{id} endpoint (which 403s for trial).
          if (endpointOverrides?.patch) {
            await quickstartPatchPlan(projectId, { items: newItems });
          } else {
            await patchContentPlanDraft(projectId, { items: newItems });
          }
          setSyncState('saved');
          setTimeout(() => setSyncState('idle'), 2000);
        } catch {
          setSyncState('error');
        }
      }, 500);
    },
    [projectId, endpointOverrides]
  );

  const updateItems = useCallback(
    (newItems: ContentPlanItem[], save = true) => {
      setItems(newItems);
      if (save) scheduleSave(newItems);
    },
    [scheduleSave]
  );

  // ── Social channel: load connected accounts on step 2, pre-fill first
  // active match per platform for rows with no account selected yet ────────
  useEffect(() => {
    if (step !== 2 || draft?.channel !== 'social') return;
    let cancelled = false;
    getSocialAccounts(projectId)
      .then((accounts: any[]) => {
        if (cancelled) return;
        const byPlatform: Record<string, any[]> = {};
        (accounts || [])
          .filter((a: any) => a.status === 'active')
          .forEach((a: any) => {
            if (!byPlatform[a.platform]) byPlatform[a.platform] = [];
            byPlatform[a.platform].push(a);
          });
        setSocialAccountsByPlatform(byPlatform);

        setItems((prev) => {
          let changed = false;
          const next = prev.map((item) => {
            if (item.item_type === 'social_post' && !item.social_account_id) {
              const match = byPlatform[item.platform || '']?.[0];
              if (match) {
                changed = true;
                return { ...item, social_account_id: match.account_id };
              }
            }
            return item;
          });
          if (changed) scheduleSave(next);
          return changed ? next : prev;
        });
      })
      .catch(() => { if (!cancelled) setSocialAccountsByPlatform({}); });
    return () => { cancelled = true; };
  }, [step, draft?.channel, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── AiMe Site channel: (re)load this project's sites on step 2 for the
  // "Publishing to" select — covers a wizard opened straight to step 2. ──
  useEffect(() => {
    if (step !== 2 || draft?.channel !== 'aime_site') return;
    let cancelled = false;
    listSiteProjects(projectId)
      .then((sites) => { if (!cancelled) setSiteProjects(sites); })
      .catch(() => { if (!cancelled) setSiteProjects([]); });
    if (draft?.site_project_id && !selectedSiteId) setSelectedSiteId(draft.site_project_id);
    return () => { cancelled = true; };
  }, [step, draft?.channel, draft?.site_project_id, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Inline edit ────────────────────────────────────────────────
  const handleCellChange = (
    rowIdx: number,
    field: keyof ContentPlanItem,
    value: string | number
  ) => {
    const updated = items.map((item, i) =>
      i === rowIdx ? { ...item, [field]: value } : item
    );
    updateItems(updated);
  };

  const handleDateChange = (rowIdx: number, localValue: string) => {
    const utcZ = localInputToUtcZ(localValue, draft?.project_tz || 'UTC');
    handleCellChange(rowIdx, 'suggested_publish_at', utcZ);
  };

  const handleDeleteRow = (rowIdx: number) => {
    updateItems(items.filter((_, i) => i !== rowIdx));
  };

  // ── Regenerate row ─────────────────────────────────────────────
  const handleRegenerateRow = async (rowId: string) => {
    if (regeneratingRowId) return;
    setRegeneratingRowId(rowId);
    try {
      const replacement = await regenerateContentPlanRow(projectId, { row_id: rowId });
      setItems((prev) =>
        prev.map((item) => (item.id === rowId ? replacement : item))
      );
      setSyncState('saved');
      setTimeout(() => setSyncState('idle'), 2000);
    } catch (err: any) {
      showToast('Failed to regenerate row. Please try again.', 'error');
    } finally {
      setRegeneratingRowId(null);
    }
  };

  // ── Drag-to-reorder ────────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent, index: number) => {
    dragSourceIndex.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = dragSourceIndex.current;
    if (sourceIndex === null || sourceIndex === targetIndex) {
      setDragOverIndex(null);
      dragSourceIndex.current = null;
      return;
    }
    const newItems = [...items];
    const [moved] = newItems.splice(sourceIndex, 1);
    newItems.splice(targetIndex, 0, moved);
    setDragOverIndex(null);
    dragSourceIndex.current = null;
    updateItems(newItems);
  };

  const handleDragEnd = () => {
    dragSourceIndex.current = null;
    setDragOverIndex(null);
  };

  // ── Save Draft (flush debounce, then close) ────────────────────
  const handleSaveDraft = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSyncState('saving');
    try {
      if (endpointOverrides?.patch) {
        await quickstartPatchPlan(projectId, { items });
      } else {
        await patchContentPlanDraft(projectId, { items });
      }
      setSyncState('saved');
    } catch {
      setSyncState('error');
    }
    onClose();
  };

  // ── Discard ────────────────────────────────────────────────────
  const handleDiscard = async () => {
    try {
      await discardContentPlanDraft(projectId);
    } catch {/* ignore */}
    onClose();
  };

  // ── Approve & Schedule ─────────────────────────────────────────
  const handleApprove = async () => {
    if (approving) return;
    setApproving(true);
    try {
      // Flush any pending save first (only when using regular SEO endpoint)
      if (!endpointOverrides?.approve && saveTimer.current) {
        clearTimeout(saveTimer.current);
        await patchContentPlanDraft(projectId, { items });
      }

      let result: ContentPlanApproveResponse;
      if (endpointOverrides?.approve) {
        result = await quickstartApprovePlan({ project_id: projectId, items });
      } else {
        result = await approveContentPlan({ project_id: projectId, items });
      }

      if (draft?.channel === 'social') {
        const skippedCount = result.skipped?.length || 0;
        const msg = `${result.publications_created} social post${result.publications_created === 1 ? '' : 's'} scheduled.` +
          (skippedCount ? ` ${skippedCount} skipped.` : '');
        showToast(msg, result.publications_created === 0 ? 'error' : 'success');
      } else {
        const skippedCount = result.skipped?.length || 0;
        const msg = `${result.tasks_created} articles scheduled across ${draft?.pillars?.length || '?'} pillars.` +
          (skippedCount ? ` ${skippedCount} skipped.` : '');
        showToast(msg, result.tasks_created === 0 ? 'error' : 'success');
      }

      if (onApproveSuccess) {
        onApproveSuccess(result);
      } else {
        onClose();
        navigate(`/scheduling/${projectId}`);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const errorKey = err?.response?.data?.detail?.error;
      if (status === 409) {
        showToast('This plan was already approved.', 'success');
        if (onApproveSuccess) {
          onApproveSuccess({ tasks_created: 0, publications_created: 0, task_ids: [], publication_ids: [] });
        } else {
          onClose();
          navigate(`/scheduling/${projectId}`);
        }
      } else if (status === 422 && errorKey === 'aime_site_site_unavailable') {
        showToast('That AiMe site is no longer available. Pick another site above and approve again.', 'error');
      } else if (status === 403 && err?.response?.data?.detail === 'Editor access required for this project') {
        // Same distinguishing detail check as handleGenerate — don't shadow the
        // unrelated 403 that require_module_access('seo') raises.
        showToast('You have view-only access to this project — ask for edit access to approve this plan.', 'error');
      } else {
        showToast('Approval failed. Please try again.', 'error');
      }
    } finally {
      setApproving(false);
    }
  };

  // ── Conflict actions ───────────────────────────────────────────
  const handleViewScheduled = () => {
    onClose();
    navigate(`/scheduling/${projectId}`);
  };

  const handleGenerateFresh = async () => {
    // Discard the approved draft, then generate a new one
    try {
      await discardContentPlanDraft(projectId);
    } catch {/* ok */}
    setConflict(null);
    handleGenerate();
  };

  // ── Project tz for display ─────────────────────────────────────
  const projectTz = draft?.project_tz || 'UTC';

  // ── Render helpers ─────────────────────────────────────────────
  const renderSyncIndicator = () => {
    if (syncState === 'idle') return null;
    const cls = `cpw-sync-indicator ${syncState}`;
    if (syncState === 'saving') return <span className={cls}>Saving…</span>;
    if (syncState === 'saved') return <span className={cls}><Check size={12} /> Saved</span>;
    if (syncState === 'error') return <span className={cls}>Save failed</span>;
    return null;
  };

  const renderDifficultyBadge = (difficulty: string) => (
    <span className={`cpw-badge ${difficulty}`}>{difficulty}</span>
  );

  // ── Step 1 content ─────────────────────────────────────────────
  const renderStep1 = () => (
    <div className="cpw-step1-form">
      {!isSocial && (
        <>
          {/* Seed keywords */}
          <div className="cpw-field">
            <label className="cpw-label">
              Seed Keywords{' '}
              <span className="cpw-label-hint">(pre-filled from your masterplan streams)</span>
            </label>
            {loadingSeeds ? (
              <div className="cpw-chip-hint">Loading masterplan streams…</div>
            ) : seeds.length === 0 && !chipInput ? (
              <div className="cpw-no-seeds">
                No masterplan found. Type keywords below to add seeds manually, or{' '}
                <strong>create a masterplan</strong> first in the Content module for
                auto-filled suggestions.
              </div>
            ) : null}
            <div className="cpw-chips-container">
              {seeds.map((seed, i) => (
                <span key={seed} className="cpw-chip">
                  {seed}
                  <button
                    type="button"
                    className="cpw-chip-remove"
                    onClick={() => removeChip(i)}
                    aria-label={`Remove ${seed}`}
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="cpw-chip-input"
                placeholder={seeds.length < 15 ? 'Add keyword, press Enter…' : 'Max 15 reached'}
                value={chipInput}
                onChange={(e) => setChipInput(e.target.value)}
                onKeyDown={handleChipKeyDown}
                onBlur={() => { if (chipInput.trim()) addChip(chipInput); }}
                disabled={seeds.length >= 15}
              />
            </div>
            <div className="cpw-chip-hint">Press Enter or comma to add. Max 15 seeds.</div>
          </div>

          {/* Locale */}
          <div className="cpw-field">
            <label className="cpw-label">Target Locale</label>
            <select
              className="cpw-select"
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
            >
              {LOCALE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {isSocial && (
        <div className="cpw-field">
          <label className="cpw-label">Unscheduled Social Posts</label>
          {loadingSocialCandidates ? (
            <div className="cpw-chip-hint">Checking your catalogue…</div>
          ) : (socialCandidates?.total_eligible ?? 0) > 0 && socialCandidates!.connected_platforms.length === 0 ? (
            <div className="cpw-low-confidence-banner">
              <AlertTriangle size={16} />
              <span>
                <strong>{socialCandidates!.total_eligible} unscheduled social posts</strong> found,
                but you have no connected social accounts for those platforms yet. Connect one in
                Social Accounts before generating, or rows without an account will need to be
                removed in step 2.
              </span>
            </div>
          ) : (socialCandidates?.total_eligible ?? 0) > 0 ? (
            <div className="cpw-chip-hint">
              <strong>{socialCandidates!.total_eligible} unscheduled social posts</strong> found
              across {socialCandidates!.connected_platforms.join(', ') || 'your platforms'}.
              {socialCandidates!.total_ineligible > 0 && (
                <> {socialCandidates!.total_ineligible} skipped — already scheduled or missing content.</>
              )}
            </div>
          ) : (
            <div className="cpw-low-confidence-banner">
              <AlertTriangle size={16} />
              <span>
                No unscheduled social posts found. Generate social posts from an article in
                Multi-Channel first.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Cadence */}
      <div className="cpw-field">
        <label className="cpw-label">Publishing Cadence</label>
        <div className="cpw-cadence-group">
          {(Object.keys(CADENCE_LABELS) as CadenceOption[]).map((c) => (
            <button
              key={c}
              type="button"
              className={`cpw-cadence-btn${cadence === c ? ' active' : ''}`}
              onClick={() => setCadence(c)}
            >
              {CADENCE_LABELS[c]}
            </button>
          ))}
        </div>
      </div>

      {/* Channel */}
      <div className="cpw-field">
        <label className="cpw-label">Publishing Channel</label>
        <select
          className="cpw-select"
          value={channel}
          onChange={(e) => setChannel(e.target.value as ChannelOption)}
        >
          {availableChannelOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {isAimeSite && (
        <div className="cpw-field">
          <label className="cpw-label">AiMe Site</label>
          {loadingSites ? (
            <div className="cpw-chip-hint">Loading your sites…</div>
          ) : eligibleSites.length === 0 ? (
            <div className="cpw-low-confidence-banner">
              <AlertTriangle size={16} />
              <span>
                No AiMe sites found for this project. Build one in Site Builder first, then come
                back and generate your plan.
              </span>
            </div>
          ) : selectableSites.length === 0 ? (
            <div className="cpw-low-confidence-banner">
              <AlertTriangle size={16} />
              <span>
                Your imported site has no blog page yet — create one in the Imported Site Editor,
                then come back.
              </span>
            </div>
          ) : (
            <div className="cpw-site-option-list">
              {eligibleSites.map((site) => {
                const disabled = needsBlogPage(site);
                const reason = disabled
                  ? 'No blog page — create one in the Imported Site Editor'
                  : `${site.page_count} pages · ${site.status}`;
                return (
                  <label
                    key={site.site_project_id}
                    className={`cpw-site-option${selectedSiteId === site.site_project_id ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
                    title={reason}
                    aria-label={`Publish to ${site.name}`}
                  >
                    <input
                      type="radio"
                      name="cpw-aime-site"
                      checked={selectedSiteId === site.site_project_id}
                      disabled={disabled}
                      onChange={() => setSelectedSiteId(site.site_project_id)}
                    />
                    <span>
                      <span className="cpw-site-option-name">{site.name}</span>
                      <span className="cpw-site-option-meta">{reason}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {generateError && (
        <div className="cpw-low-confidence-banner">
          <AlertTriangle size={16} />
          <span>{generateError}</span>
        </div>
      )}

      {conflict && (
        <div className="cpw-conflict-notice">
          <p>
            <strong>A plan was already approved</strong> on{' '}
            {conflict.approved_at
              ? new Date(conflict.approved_at).toLocaleDateString()
              : 'an earlier date'}
            .
          </p>
          <div className="cpw-conflict-actions">
            <GlassButton variant="primary" size="sm" onClick={handleViewScheduled}>
              View Scheduled Articles
            </GlassButton>
            <GlassButton variant="secondary" size="sm" onClick={handleGenerateFresh}>
              Generate Fresh Plan
            </GlassButton>
          </div>
        </div>
      )}
    </div>
  );

  // ── Step 2 content ─────────────────────────────────────────────
  const isSocialDraft = draft?.channel === 'social';
  const isAimeSiteDraft = draft?.channel === 'aime_site';
  const missingAccountCount = isSocialDraft
    ? items.filter((it) => !it.social_account_id).length
    : 0;
  const missingSiteCount = isAimeSiteDraft
    ? items.filter((it) => !it.site_project_id).length
    : 0;

  // Rewrites site_project_id on every item — one debounced PATCH, not one per
  // row. This is the recovery affordance for the all-skipped 422: without it,
  // a user whose site was deleted mid-flight would have to discard and
  // regenerate the whole plan (and pay for the SERP calls again).
  const handleChangeSite = (siteId: string) => {
    setSelectedSiteId(siteId);
    const next = items.map((item) => ({ ...item, site_project_id: siteId }));
    updateItems(next);
    const pickedSite = selectableSites.find((s) => s.site_project_id === siteId)
      || siteProjects.find((s) => s.site_project_id === siteId);
    setDraft((d) => (d ? { ...d, site_project_id: siteId, site_name: pickedSite?.name || d.site_name } : d));
  };

  const renderStep2 = () => (
    <>
      {/* Low-confidence banner */}
      {draft?.low_confidence && (
        <div className="cpw-low-confidence-banner">
          <AlertTriangle size={16} />
          <span>
            SERP data unavailable — using AI clustering only. Quality may vary.
          </span>
          <div className="cpw-why-tooltip">
            <button
              type="button"
              className="cpw-why-tooltip-trigger"
              onClick={() => setShowTooltip((v) => !v)}
            >
              Why?
            </button>
            {showTooltip && (
              <div className="cpw-why-tooltip-content">
                Your SERP provider credentials (SERPAPI_KEY or DATAFORSEO credentials) are
                not configured, or the provider returned an error. Topics were clustered from
                your seed keywords alone using Gemini AI without live search data.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Truncated candidate notice */}
      {!!draft?.truncated_candidate_count && (
        <div className="cpw-low-confidence-banner">
          <AlertTriangle size={16} />
          <span>
            {draft.truncated_candidate_count} older {isSocialDraft ? 'post' : 'article'}
            {draft.truncated_candidate_count === 1 ? '' : 's'} {draft.truncated_candidate_count === 1 ? 'was' : 'were'} left
            out (60-post limit) — approve this batch, then generate again to pick up the rest.
          </span>
        </div>
      )}

      {/* Summary stats */}
      <div className="cpw-review-header">
        <div className="cpw-review-stats">
          <span className="cpw-stat-chip">
            {draft?.pillars?.length || 0} {isSocialDraft ? 'platforms' : 'pillars'}
          </span>
          <span className="cpw-stat-chip">{items.length} {isSocialDraft ? 'social posts' : 'articles'}</span>
          <span className="cpw-stat-chip">
            {draft?.cadence ? CADENCE_LABELS[draft.cadence as CadenceOption] || draft.cadence : ''} cadence
          </span>
          <span className="cpw-stat-chip">
            Channel: {draft?.channel}
          </span>
          {isAimeSiteDraft && (
            <>
              <span className="cpw-stat-chip">Site: {draft?.site_name || selectedSiteId}</span>
              {selectableSites.length > 0 && (
                <label className="cpw-field" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span className="cpw-label" style={{ marginBottom: 0 }}>Publishing to</span>
                  <select
                    className="cpw-cell-select"
                    value={selectedSiteId}
                    onChange={(e) => handleChangeSite(e.target.value)}
                  >
                    {selectableSites.map((s) => (
                      <option key={s.site_project_id} value={s.site_project_id}>{s.name}</option>
                    ))}
                  </select>
                </label>
              )}
            </>
          )}
        </div>
        {renderSyncIndicator()}
      </div>

      {/* Items table */}
      <div className="cpw-table-wrap">
        <table className="cpw-table">
          <thead>
            {isSocialDraft ? (
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Publish Date ({projectTz})</th>
                <th>Platform</th>
                <th>Post</th>
                <th>Source Article</th>
                <th>Account</th>
                <th>Actions</th>
              </tr>
            ) : (
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Publish Date ({projectTz})</th>
                <th>Title</th>
                <th>Target Keyword</th>
                <th>Pillar</th>
                <th>Words</th>
                <th>Difficulty</th>
                <th>Top Competitors</th>
                <th>Actions</th>
              </tr>
            )}
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr
                key={item.id}
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={handleDragEnd}
                className={`${dragOverIndex === idx ? 'drag-over' : ''} ${
                  dragSourceIndex.current === idx ? 'dragging' : ''
                }`}
              >
                {/* Drag handle */}
                <td>
                  <span className="cpw-drag-handle" title="Drag to reorder">
                    <GripVertical size={14} />
                  </span>
                </td>

                {/* Date */}
                <td>
                  <input
                    type="datetime-local"
                    className="cpw-date-input"
                    value={utcToLocalInput(item.suggested_publish_at, projectTz)}
                    onChange={(e) => handleDateChange(idx, e.target.value)}
                    title={item.suggested_publish_at}
                  />
                </td>

                {isSocialDraft ? (
                  <>
                    {/* Platform (read-only) */}
                    <td>
                      <span className="cpw-cell-static">{item.platform}</span>
                    </td>

                    {/* Post preview (read-only — text is snapshotted from the catalogue) */}
                    <td>
                      <span className="cpw-cell-static" title={item.content_preview}>
                        {item.content_preview || item.title}
                      </span>
                    </td>

                    {/* Source article (read-only) */}
                    <td>
                      <span className="cpw-cell-static">{item.source_article || '—'}</span>
                    </td>

                    {/* Account picker */}
                    <td>
                      {(socialAccountsByPlatform[item.platform || ''] || []).length > 0 ? (
                        <select
                          className="cpw-cell-select"
                          aria-label={`Publishing account for ${item.platform}`}
                          value={item.social_account_id || ''}
                          onChange={(e) => handleCellChange(idx, 'social_account_id', e.target.value)}
                        >
                          {(socialAccountsByPlatform[item.platform || ''] || []).map((a) => (
                            <option key={a.account_id} value={a.account_id}>
                              {a.display_name || a.username || a.account_id}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="cpw-cell-empty">
                          No {item.platform} account — connect one
                        </span>
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    {/* Title */}
                    <td>
                      <input
                        type="text"
                        className="cpw-cell-input"
                        value={item.title}
                        onChange={(e) => handleCellChange(idx, 'title', e.target.value)}
                        onBlur={() => scheduleSave(items)}
                        style={{ minWidth: 180 }}
                      />
                    </td>

                    {/* Target keyword */}
                    <td>
                      <input
                        type="text"
                        className="cpw-cell-input"
                        value={item.target_keyword}
                        onChange={(e) => handleCellChange(idx, 'target_keyword', e.target.value)}
                        onBlur={() => scheduleSave(items)}
                        style={{ minWidth: 120 }}
                      />
                    </td>

                    {/* Pillar */}
                    <td>
                      <select
                        className="cpw-cell-select"
                        value={item.pillar}
                        onChange={(e) => handleCellChange(idx, 'pillar', e.target.value)}
                      >
                        {(draft?.pillars || []).map((p) => (
                          <option key={p.name} value={p.name}>{p.name}</option>
                        ))}
                      </select>
                    </td>

                    {/* Word count */}
                    <td>
                      <input
                        type="number"
                        className="cpw-cell-input cpw-cell-input-sm"
                        value={item.target_word_count}
                        min={200}
                        max={5000}
                        step={100}
                        onChange={(e) =>
                          handleCellChange(idx, 'target_word_count', parseInt(e.target.value, 10))
                        }
                        onBlur={() => scheduleSave(items)}
                      />
                    </td>

                    {/* Difficulty badge (read-only) */}
                    <td>{renderDifficultyBadge(item.difficulty)}</td>

                    {/* Top competitors */}
                    <td>
                      {item.competitors && item.competitors.length > 0 ? (
                        <div className="cpw-competitor-urls">
                          {item.competitors.slice(0, 2).map((url) => {
                            try {
                              return (
                                <span key={url} className="cpw-competitor-url" title={url}>
                                  {new URL(url).hostname}
                                </span>
                              );
                            } catch {
                              return <span key={url} className="cpw-competitor-url">{url}</span>;
                            }
                          })}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>—</span>
                      )}
                    </td>
                  </>
                )}

                {/* Actions */}
                <td>
                  <div className="cpw-row-actions">
                    {!isSocialDraft && (
                      <button
                        type="button"
                        className={`cpw-icon-btn${regeneratingRowId === item.id ? ' regenerating' : ''}`}
                        onClick={() => handleRegenerateRow(item.id)}
                        disabled={!!regeneratingRowId}
                        title="Regenerate this row with fresh SERP data"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="cpw-icon-btn danger"
                      onClick={() => handleDeleteRow(idx)}
                      title="Delete this row"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  // ── Modal title ────────────────────────────────────────────────
  const modalTitle =
    step === 1 ? 'Generate 30-Day Content Plan' : 'Review & Approve Your Content Plan';

  // ── Footer ─────────────────────────────────────────────────────
  const renderFooter = () => {
    if (generating) {
      return (
        <div className="cpw-footer">
          <div className="cpw-footer-left">
            <GlassButton variant="secondary" onClick={onClose}>
              Cancel
            </GlassButton>
          </div>
          <div className="cpw-footer-right">
            <GlassButton variant="primary" disabled>
              Generating…
            </GlassButton>
          </div>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className="cpw-footer">
          <div className="cpw-footer-left">
            <GlassButton variant="secondary" onClick={onClose}>
              Cancel
            </GlassButton>
          </div>
          <div className="cpw-footer-right">
            <GlassButton
              variant="primary"
              onClick={handleGenerate}
              disabled={
                isSocial ? (socialCandidates?.total_eligible ?? 0) === 0
                : isAimeSite ? (seeds.length === 0 || !selectedSiteId)
                : seeds.length === 0
              }
            >
              Generate Plan
            </GlassButton>
          </div>
        </div>
      );
    }

    // Step 2
    return (
      <div className="cpw-footer">
        <div className="cpw-footer-left">
          <GlassButton variant="danger" onClick={handleDiscard}>
            Discard
          </GlassButton>
        </div>
        <div className="cpw-footer-right">
          <GlassButton variant="secondary" onClick={handleSaveDraft}>
            Save Draft
          </GlassButton>
          <GlassButton
            variant="primary"
            onClick={handleApprove}
            disabled={
              approving ||
              items.length === 0 ||
              (isSocialDraft && items.some((it) => !it.social_account_id)) ||
              (isAimeSiteDraft && missingSiteCount > 0)
            }
            title={
              isSocialDraft && missingAccountCount > 0
                ? `${missingAccountCount} post${missingAccountCount === 1 ? '' : 's'} need an account connected or removed before you can approve.`
                : isAimeSiteDraft && missingSiteCount > 0
                ? 'Pick an AiMe site for every article first.'
                : undefined
            }
          >
            {approving ? 'Approving…' : 'Approve & Schedule'}
          </GlassButton>
        </div>
      </div>
    );
  };

  // ── Shared body content (used in both modal and inline modes) ──
  const wizardBody = (
    <>
      {/* Step indicator (hide in inline+autoStart mode when on step 2) */}
      {!(inline && autoStart && step === 2) && (
        <div className="cpw-steps">
          <div className={`cpw-step${step === 1 ? ' active' : ' complete'}`}>
            <span className="cpw-step-num">1</span>
            <span>Inputs</span>
          </div>
          <div className="cpw-step-divider" />
          <div className={`cpw-step${step === 2 ? ' active' : ''}`}>
            <span className="cpw-step-num">2</span>
            <span>Review & Approve</span>
          </div>
        </div>
      )}

      {/* Body */}
      {generating ? (
        <div className="cpw-loading">
          <div className="cpw-loading-spinner" />
          <span>{forceGeminiOnly ? 'Building your plan with AI…' : 'Fetching SERP data, clustering with AI…'}</span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
            This may take 20–40 seconds
          </span>
        </div>
      ) : step === 1 ? (
        renderStep1()
      ) : (
        renderStep2()
      )}
    </>
  );

  // ── Main render ────────────────────────────────────────────────
  return (
    <>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 99999,
            padding: '12px 20px',
            background: toast.kind === 'error' ? 'var(--color-danger, #ef4444)' : 'var(--color-success, #1eaca6)',
            color: '#fff',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            fontSize: 'var(--text-sm)',
            maxWidth: '360px',
          }}
        >
          {toast.msg}
        </div>
      )}

      {inline ? (
        // Inline mode — render directly in parent container, no modal chrome
        <div className="cpw-inline-container">
          <div className="cpw-inline-header">
            <h3 className="cpw-inline-title">{modalTitle}</h3>
          </div>
          <div className="cpw-inline-body">
            {wizardBody}
          </div>
          <div className="cpw-inline-footer">
            {renderFooter()}
          </div>
        </div>
      ) : (
        <GlassModal
          isOpen={isOpen}
          onClose={onClose}
          title={modalTitle}
          size="xl"
          className="content-plan-wizard-modal"
          footer={renderFooter()}
          showCloseButton
        >
          {wizardBody}
        </GlassModal>
      )}
    </>
  );
};

export default ContentPlanWizard;
